import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeftIcon, 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ClockIcon,
  UserGroupIcon,
  UserIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { workflowService, type WorkflowStep } from '../services/workflowService';
import { Button } from '../components/common/Button';
import { Card, CardContent } from '../components/common/Card';
import { Badge } from '../components/common/Badge';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useNotification } from '../contexts/NotificationContext';
import WorkflowForm from '../components/admin/WorkflowForm';

export const WorkflowDetail = () => {
  const { id } = useParams<{ id: string }>();
  const workflowId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<WorkflowStep | null>(null);
  const [isDeleteStepDialogOpen, setIsDeleteStepDialogOpen] = useState(false);

  // Fetch workflow details
  const { 
    data: workflow, 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: () => workflowService.getWorkflow(workflowId),
    enabled: !!workflowId && !isNaN(workflowId)
  });

  // Update workflow mutation
  const updateWorkflowMutation = useMutation({
    mutationFn: (data: any) => workflowService.updateWorkflow(workflowId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
      setIsEditModalOpen(false);
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Workflow updated successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to update workflow'
      });
    }
  });

  // Delete workflow mutation
  const deleteWorkflowMutation = useMutation({
    mutationFn: () => workflowService.deleteWorkflow(workflowId),
    onSuccess: () => {
      navigate('/workflows');
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Workflow deleted successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to delete workflow'
      });
    }
  });

  // Delete step mutation
  const deleteStepMutation = useMutation({
    mutationFn: (stepId: number) => 
      workflowService.deleteWorkflowStep(workflowId, stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] });
      setIsDeleteStepDialogOpen(false);
      showNotification({
        type: 'success',
        title: 'Success',
        message: 'Step deleted successfully'
      });
    },
    onError: (error: any) => {
      showNotification({
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.error || 'Failed to delete step'
      });
    }
  });

  const handleUpdateWorkflow = (data: any) => {
    updateWorkflowMutation.mutate(data);
  };

  const handleDeleteWorkflow = () => {
    deleteWorkflowMutation.mutate();
  };

  const handleDeleteStep = () => {
    if (currentStep?.id) {
      deleteStepMutation.mutate(currentStep.id);
    }
  };

  const openDeleteStepDialog = (step: WorkflowStep) => {
    setCurrentStep(step);
    setIsDeleteStepDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !workflow) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4">
          Error loading workflow. Please try again.
        </div>
        <Button
          onClick={() => navigate('/workflows')}
          variant="secondary"
          className="mt-4"
        >
          Back to workflows
        </Button>
      </div>
    );
  }

  // Helper function to get approver icon based on type
  const getApproverIcon = (type: string) => {
    switch (type) {
      case 'manager':
        return <UserIcon className="h-5 w-5 text-blue-500" />;
      case 'role':
        return <UserGroupIcon className="h-5 w-5 text-purple-500" />;
      case 'user':
        return <UserIcon className="h-5 w-5 text-green-500" />;
      default:
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Button
            onClick={() => navigate('/workflows')}
            variant="ghost"
            className="mr-4"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{workflow.name}</h1>
        </div>
        <div className="flex space-x-3">
          <Button
            onClick={() => setIsEditModalOpen(true)}
            variant="secondary"
            className="flex items-center"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Edit
          </Button>
          <Button
            onClick={() => setIsDeleteDialogOpen(true)}
            variant="danger"
            className="flex items-center"
          >
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-4">
              <h2 className="font-medium text-lg mb-4">Workflow Details</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Status</h3>
                  <Badge color={workflow.is_active ? 'green' : 'gray'} className="mt-1">
                    {workflow.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>

                {workflow.description && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Description</h3>
                    <p className="mt-1 text-gray-900">{workflow.description}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Created</h3>
                  <p className="mt-1 text-gray-900">
                    {new Date(workflow.created_at || '').toLocaleDateString()}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                  <p className="mt-1 text-gray-900">
                    {new Date(workflow.updated_at || '').toLocaleDateString()}
                  </p>
                </div>

                {workflow.leaveTypes && workflow.leaveTypes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Assigned to Leave Types</h3>
                    <div className="mt-1 space-y-1">
                      {workflow.leaveTypes.map((type: any) => (
                        <Badge key={type.id} color="blue">
                          {type.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Card>
            <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
              <h2 className="font-medium">Approval Steps</h2>
              <Badge color="blue">{workflow.steps?.length || 0} steps</Badge>
            </div>
            <CardContent className="p-0">
              {!workflow.steps || workflow.steps.length === 0 ? (
                <div className="p-8 text-center">
                  <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No steps defined</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    This workflow doesn't have any approval steps yet.
                  </p>
                  <div className="mt-6">
                    <Button
                      onClick={() => setIsEditModalOpen(true)}
                      className="inline-flex items-center"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Add Steps
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="divide-y">
                  {workflow.steps.map((step, index) => (
                    <div key={step.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between">
                        <div className="flex items-start">
                          <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 mr-3">
                            {index + 1}
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-gray-900">
                              {step.name}
                            </h3>
                            {step.description && (
                              <p className="mt-1 text-sm text-gray-500">
                                {step.description}
                              </p>
                            )}
                            <div className="mt-2 flex items-center">
                              {getApproverIcon(step.approver_type)}
                              <span className="ml-1 text-xs text-gray-500 capitalize">
                                {step.approver_type}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => openDeleteStepDialog(step)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Workflow Modal */}
      {isEditModalOpen && (
        <WorkflowForm
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdateWorkflow}
          isLoading={updateWorkflowMutation.isPending}
          title="Edit Workflow"
          workflow={workflow}
        />
      )}

      {/* Delete Workflow Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Workflow"
        message="Are you sure you want to delete this workflow? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteWorkflow}
        onCancel={() => setIsDeleteDialogOpen(false)}
        isLoading={deleteWorkflowMutation.isPending}
        variant="danger"
      />

      {/* Delete Step Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteStepDialogOpen}
        title="Delete Step"
        message={`Are you sure you want to delete the step "${currentStep?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteStep}
        onCancel={() => setIsDeleteStepDialogOpen(false)}
        isLoading={deleteStepMutation.isPending}
        variant="danger"
      />
    </div>
  );
};

export default WorkflowDetail;